import GridTable from '@/components/table/GridTable'
import { HStack } from '@musinsa/mwms-ds'
import { InventoryDefectHistoryDetailItem, InventoryDefectHistoryDetailReqParams } from '@/services/inventory/inventory.types'
import MAlert from '@/utils/modal/MAlert'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { INVENTORY_DEFECT_HISTORY_DETAIL_EXCEL_URL } from '@/services/inventory/apis'
import { EXCEL_NO_DATA_MESSAGE } from '@/pages/inventory/inventoryHistory/consts'
import { useState } from 'react'
import { ExcelDownloadReasonModal } from '@/pages/inventory/inventoryHistory/components/ExcelDownloadReasonModal'
import { ColDef } from 'ag-grid-community'
import { GRID_SIZE } from '@/utils/grid/grid'

interface InventoryDefectHistoryDetailTableProps {
  data: InventoryDefectHistoryDetailItem[]
  isLoading?: boolean
  downloadReasonOptions: { value: string; label: string }[]
  /** 그리드에 보이는 데이터를 조회한 조건. 엑셀은 이 조건으로 BE 에서 다시 만든다. */
  excelQuery?: InventoryDefectHistoryDetailReqParams
}
export const InventoryDefectHistoryDetailTable = ({ data, isLoading, downloadReasonOptions, excelQuery }: InventoryDefectHistoryDetailTableProps) => {
  const [isDownloadOpen, setIsDownloadOpen] = useState(false)
  const { onDownloadExcel } = useExcelDownload({ fileNamePrefix: '불량수불내역_상세' })
  const columnDefs: ColDef<InventoryDefectHistoryDetailItem>[] = [
    { field: 'gdsCd', headerName: '상품코드', width: 160 },
    { field: 'gdsNm', headerName: '상품명', width: 240 },
    { field: 'brdNm', headerName: '브랜드명', width: 160 },
    { field: 'bohQty', headerName: '기초재고수량', cellDataType: 'decimal', width: 140 },
    { field: 'bohUnitPr', headerName: '기초재고단가', cellDataType: 'decimal', width: 140 },
    { field: 'bohPr', headerName: '기초재고금액', cellDataType: 'integerDecimal', width: 140 },
    { field: 'anOrdQty', headerName: '입고수량', cellDataType: 'decimal', width: 140 },
    { field: 'anOrdUnitPr', headerName: '입고단가', cellDataType: 'decimal', width: 140 },
    { field: 'anPr', headerName: '입고금액', cellDataType: 'integerDecimal', width: 140 },
    { field: 'doOrdQty', headerName: '출고수량', cellDataType: 'decimal', width: 140 },
    { field: 'doOrdUnitPr', headerName: '출고단가', cellDataType: 'decimal', width: 140 },
    { field: 'doPr', headerName: '출고금액', cellDataType: 'integerDecimal', width: 140 },
    { field: 'eohQty', headerName: '기말재고수량', cellDataType: 'decimal', width: 140 },
    { field: 'eohUnitPr', headerName: '기말재고단가', cellDataType: 'decimal', width: 140 },
    { field: 'eohPr', headerName: '기말재고금액', cellDataType: 'integerDecimal', width: 140 },
  ]

  return (
    <HStack full gap={16}>
      <GridTable<InventoryDefectHistoryDetailItem>
        gridKey="IV03_ITEM"
        rowData={data}
        isLoading={isLoading}
        countPerPage={GRID_SIZE.DEFAULT}
        columnDefs={columnDefs}
        totalCount={data.length}
        useColumnFilter
        onExportData={() => {
          if (!data.length || !excelQuery) {
            MAlert.show(EXCEL_NO_DATA_MESSAGE)
            return
          }
          setIsDownloadOpen(true)
        }}
      />
      <ExcelDownloadReasonModal
        open={isDownloadOpen}
        reasonOptions={downloadReasonOptions}
        onConfirm={({ reasonCode, reasonDetail }) => {
          setIsDownloadOpen(false)
          // 감사로그(EXPORT)는 BE 가 쿼리의 reasonCode·detailedReason 으로 남긴다(LOGISPRD-13945).
          // qsStringify 가 encode:false 라 자유입력 사유는 직접 인코딩한다 — 안 하면 `&`·`#` 에서 사유가 잘린다.
          // 엑셀 컬럼 구성은 조회 대상 화주가 아니라 로그인 세션 화주로 정해진다.
          onDownloadExcel(INVENTORY_DEFECT_HISTORY_DETAIL_EXCEL_URL, { ...excelQuery, viewerShipperCode: AuthStorage.user.get().SPR_NM ?? '', reasonCode: encodeURIComponent(reasonCode), detailedReason: encodeURIComponent(reasonDetail) })
        }}
        onCancel={() => setIsDownloadOpen(false)}
      />
    </HStack>
  )
}
